/**
 * 3D Designer — every piece of English the designer and its viewer chrome show.
 *
 * The designer is super-admin / dev-only while it is being proven on
 * `3d-visualizer-v3`, so these are not in the locale dictionaries yet. They are
 * kept in one place so the translation pass (when the designer is opened to
 * visitors) is a mechanical move into public/locales rather than a hunt.
 */

import { SPACE_TYPES } from '../../data/presets';
export const DESIGNER_STRINGS = {
  title: '3D Designer',
  sandboxTitle: '3D Designer — sandbox',
  close: 'Close designer',
  backToEstimator: 'Estimator',

  // Viewer chrome (edit mode)
  exitEditMode: '✕ Exit Edit Mode',
  hintLocked: 'Editing — click a section to select it | Arrow keys nudge 6" (Ctrl 1", Shift 1 ft)',
  hintOrbit: 'Drag to rotate',
  hintLookAround: 'Drag to look around | Double-click the floor to move there',
  hintWheelZoom: 'Scroll to zoom',
  hintZoom: 'Ctrl+Scroll to zoom',
  hintEyeHeight: 'Ctrl+Scroll to raise or lower your eye',
  hintLock: 'Click a wall or the ceiling to edit it',
  framingShow: 'Show Framing',
  framingHide: 'Hide Framing',
  framingShowTitle: 'Show 2x4 framing',
  framingHideTitle: 'Hide 2x4 framing',

  // Editing
  addSection: 'Add a section here',
  // The "+" menu: ready-made ways to fill a bare area
  quickLayoutTitle: 'Add to this area',
  quickLayout: {
    'single': 'Single section',
    'halves': 'Two bands',
    'slatwall-band-white': 'SlatWall band, white',
    'slatwall-band-gray': 'SlatWall band, gray',
  },
  quickLayoutHint: {
    'single': 'One Wall&CeilingBoard section filling the area',
    'halves': 'Top: Wall&CeilingBoard, vertical, white · Bottom: Wall&CeilingBoard, horizontal, gray',
    'slatwall-band-white': 'Top and bottom: Wall&CeilingBoard, vertical, white · Middle: SlatWall, gray',
    'slatwall-band-gray': 'Top and bottom: Wall&CeilingBoard, vertical, gray · Middle: SlatWall, white',
  },
  quickLayoutTooSmall: 'This area is not tall enough for these bands',
  deleteSection: 'Delete section',
  deleteSections: 'Delete selected sections',
  duplicate: 'Duplicate (Ctrl+D)',
  arrange: 'Align and distribute',
  arrangeRelativeTo: 'Relative to',
  arrangeWall: 'Wall',
  arrangeCeiling: 'Ceiling',
  arrangeSelection: 'Selection',
  align: 'Align',
  alignLeft: 'Align left edges',
  alignHCenter: 'Center left to right',
  alignRight: 'Align right edges',
  alignTop: 'Align top edges',
  alignVCenter: 'Center top to bottom',
  alignBottom: 'Align bottom edges',
  distribute: 'Distribute',
  distributeH: 'Distribute left to right',
  distributeV: 'Distribute top to bottom',
  spacingAround: 'Equal margins',
  spacingAroundTitle: 'The same gap at both ends as between the sections',
  spacingBetween: 'Edge to edge',
  spacingBetweenTitle: 'First and last touch the edges, equal gaps between',
  multiHint: 'Shift-click sections to select more than one.',
  selectedCount: (n: number) => `${n} sections selected`,
  selectedAccessories: (n: number) => `${n} accessories selected`,
  arrangeSlatWall: 'SlatWall',
  noRoomToDistribute: 'There isn’t enough room to spread these out.',
  undo: 'Undo (Ctrl+Z)',
  redo: 'Redo (Ctrl+Y)',
  undoLabel: 'Undo',
  redoLabel: 'Redo',
  prevSurface: 'Previous wall',
  nextSurface: 'Next wall',
  chooseSurface: 'Go to a wall or the ceiling',
  // Export for the Blender renderer (scripts/blender)
  exportRender: 'Render',
  exportRenderTitle: 'Save the room as a .glb with its camera and lights, for the Blender renderer',
  exportRenderNotReady: 'The room is still being built — try again in a moment.',
  fieldX: 'X',
  /** The button that unfolds the X/Y/W/H boxes on a phone. */
  sizeFields: 'Position & size',
  fieldY: 'Y',
  fieldWidth: 'W',
  fieldHeight: 'H',
  fieldXTitle: 'Left edge — distance from the left of the wall',
  fieldYTitle: 'Top edge — distance down from the top of the wall',
  fieldYTitleCeiling: 'Top edge — distance down from the top of the view',
  fieldWidthTitle: 'Width',
  fieldHeightTitle: 'Height',
  horizontal: 'Boards run horizontally',
  vertical: 'Boards run vertically',
  ceilingAlongLength: 'Boards run along the room’s length',
  ceilingAlongWidth: 'Boards run along the room’s width',
  phoneViewOnly: 'Editing needs a larger screen — rotate and look around here.',
  noProducts: 'This retailer has no products for a new section.',
  layoutIssues: (n: number) => `${n} section${n === 1 ? '' : 's'} need${n === 1 ? 's' : ''} attention`,
  /** Why an edit was refused — keyed by the reason codes in calculations/sections. */
  refused: {
    'too-small': 'A section can’t be smaller than 6".',
    'out-of-bounds': 'That would run past the edge of the wall.',
    overlap: 'That would overlap another section of the same product.',
    'slatwall-rows': 'SlatWall height goes in 6" rows.',
    'slatwall-on-ceiling': 'SlatWall can’t go on a ceiling.',
    'not-found': 'That section no longer exists.',
    'duplicate-id': 'That section already exists.',
    'no-room': 'There isn’t enough empty space on this wall for that.',
    'too-close-to-edge': 'That is too close to the edge of the wall.',
    'not-on-floor': 'This door is pinned to the floor. Turn off “On floor” to move it up or down.',
    'passage-fixed': 'This is where a shared wall was removed. Restore the wall in the Floor Planner to change it.',
    'straddles-shared-wall': 'On a wall two rooms share, a window or door has to sit wholly inside the shared stretch (to be in both rooms) or wholly outside it.',
    'other-room': 'That would not fit the wall of the room on the other side.',
    'opening-on-ceiling': 'Windows and doors go on walls.',
    'off-section': 'An accessory has to sit wholly on its SlatWall.',
    opening: 'That would sit over a window or door.',
    'not-slatwall': 'Accessories hang on SlatWall only.',
  } as Record<string, string>,

  // What an edge of the selected section meets (shown on hover, and in the key)
  edgeKey: 'Click an edge to choose its trim. Edges meet:',
  edgeKeyPerimeter: 'wall edge',
  edgeKeySection: 'another section',
  edgeKeyBare: 'bare wall',
  edgeSide: { top: 'Top', right: 'Right', bottom: 'Bottom', left: 'Left' } as Record<string, string>,
  edgePerimeterWall: { top: 'the ceiling line', bottom: 'the floor', left: 'the corner', right: 'the corner' } as Record<string, string>,
  edgePerimeterCeiling: 'the wall',
  edgeBare: 'bare wall',
  openingName: { window: 'Window', door: 'Door', garageDoor: 'Garage door', passage: 'Open wall' } as Record<string, string>,
  addOpening: 'Add',
  copyLayout: 'Copy layout to other walls',
  copyLayoutHint: (wall: string) => `Lays ${wall}'s sections onto the walls you pick, at the same heights from the floor (the top section meets each wall's ceiling), so bands line up round the room.`,
  copyTo: 'Copy to',
  copyToAll: 'All other walls',
  copyInclude: 'Include',
  copyIncludeKind: (kind: string) => `${kind}s`,
  copyWarning: 'Replaces whatever is on the chosen walls. Undo brings it back.',
  copyNow: (n: number) => (n === 0 ? 'Copy' : n === 1 ? 'Copy to 1 wall' : `Copy to ${n} walls`),
  addOpeningOf: (name: string) => `Add a ${name.toLowerCase()}`,
  deleteOpening: 'Delete',
  openingSize: 'Garage door size',
  openingStyle: 'Style',
  hinge: { left: 'Hinges left', right: 'Hinges right' } as Record<string, string>,
  hingeTitle: 'Which side the hinges are on, seen from this room. Click to swap.',
  swing: { in: 'Opens in', out: 'Opens out' } as Record<string, string>,
  swingTitle: 'Opens into this room, or away from it. Click to swap.',
  floorPinned: 'On floor',
  floorPinnedOn: 'Pinned to the floor. Click to free it, so it can be moved up and down like a window.',
  floorPinnedOff: 'Free to move. Click to stand it back on the floor.',
  /** Names of the styles in room3d/openingStyles.ts, by id. */
  openingStyleName: {
    'window-hung-white': 'Single-hung — white',
    'window-hung-black': 'Single-hung — black',
    'window-picture-white': 'Picture — white',
    'window-picture-black': 'Picture — black',
    'window-picture-bronze': 'Picture — bronze',
    'window-grid-white': 'Colonial grid — white',
    'window-grid-black': 'Colonial grid — black',
    'window-prairie-white': 'Prairie — white',
    'window-prairie-black': 'Prairie — black',
    'window-fractional-white': 'Fractional — white',
    'window-fractional-black': 'Fractional — black',
    'window-diamond-white': 'Diamond — white',
    'window-diamond-black': 'Diamond — black',
    'window-traditional-hung-white': 'Traditional hung — white',
    'window-traditional-hung-black': 'Traditional hung — black',
    'window-prairie-hung-white': 'Prairie hung — white',
    'window-prairie-hung-black': 'Prairie hung — black',
    'window-grid-hung-white': 'Divided hung — white',
    'window-grid-hung-black': 'Divided hung — black',
    'door-six-panel-white': 'Six-panel — white',
    'door-shaker-walnut': 'Shaker — walnut',
    'door-half-glass-black': 'Half-glass — black',
    'door-flush-slate': 'Flush — slate',
    'door-craftsman-oak': 'Craftsman, three lites — oak',
    'door-craftsman-six-red': 'Craftsman, six lites — red',
    'door-slab-lites-black': 'Modern slab, frosted lites — black',
    'door-three-quarter-black': 'Three-quarter glass — black',
    'door-three-quarter-oak': 'Three-quarter glass — oak',
    'door-prairie-walnut': 'Prairie nine-lite — walnut',
    'garage-raised-white': 'Raised panel — white',
    'garage-raised-charcoal': 'Raised panel — charcoal',
    'garage-carriage-walnut': 'Carriage — walnut',
    'garage-full-view-black': 'Full-view glass — black',
    'garage-planks-cedar': 'Modern planks — cedar',
    'garage-x-brace-white': 'Carriage, cross-braced — white',
    'garage-x-brace-grey': 'Carriage, cross-braced — grey wood',
    'garage-barn-cedar': 'Barn, cross-braced — cedar',
  } as Record<string, string>,
  openingCustomSize: 'Custom size',
  safeMargin: 'Safe margin — windows and doors stay inside this line',
  edgeKeyOpening: 'window / door',
  edgeCovered: 'hidden under an inset — nothing to trim here',
  edgeGap: (inches: number) => ` (${inches}" gap)`,

  // Trim on an edge
  edgeTrimTitle: (side: string) => `${side} edge trim`,
  edgeTrimDefault: 'Default',
  edgeTrimNone: 'No trim',
  edgeTrimHint: 'Click an edge of the selected section to choose its trim.',
  trimNone: {
    covered: 'no trim — hidden under an inset',
    shared: 'no trim of its own — the neighbour’s trim covers this line',
    lap: 'no trim — the SlatWall laps the boards below',
    override: 'no trim (your choice)',
    floor: 'no trim — it stands on the floor',
    bare: 'no trim — nothing to finish against bare wall',
  } as Record<string, string>,
  trimChosen: ' (your choice)',

  // Live readout
  readoutWcb: 'Wall&CeilingBoard panels',
  readoutSlatWall: 'SlatWall panels',
  readoutTrim: 'Trim pieces',
  readoutScrews: 'Screws',
  readoutCost: 'Materials est.',
  readoutAccessories: 'SlatWall accessories',
  // Accessories on a SlatWall section (edit mode)
  accessories: {
    title: 'Add SlatWall accessories',
    hint: 'Click one to hang it on the selected SlatWall. Drag it to move it; Delete takes it off.',
    noRoom: 'There is no room for that on this SlatWall.',
    deleteOne: 'Take this accessory off the wall',
    selected: (label: string) => `${label} — drag to move`,
  },
  // Staging
  spaceType: 'Space',
  // The space types' names: data/presets.ts SPACE_TYPES, the one list.
  spaceTypeName: Object.fromEntries(SPACE_TYPES.map((t) => [t.id, t.label])) as Record<string, string>,
  stage: 'Stage the rooms',
  stageTitle: 'Furnish every room with props for this kind of space — on the floor, and hung from SlatWall accessories (which count in the materials).',
  restage: 'Stage again',
  clearStaging: 'Clear staging',
  stageDialog: {
    title: 'Stage the rooms',
    body: 'How full the rooms are. The main pieces (the washer, the workbench, the vanity) are there whatever you choose; the rest is thinned or multiplied. Every Go is a new arrangement.',
    sparse: 'Sparse',
    dense: 'Dense',
    density: 'Density',
    readout: (d: number) => (d === 0 ? 'Only the main pieces.' : d === 1 ? 'The kits as they are.' : d < 1 ? `About ${Math.round(d * 100)}% of the rest.` : `${d}× the rest.`),
    go: 'Go',
    close: 'Close',
  },
  previewModelsOn: 'Model previews: on',
  previewModelsOff: 'Model previews: off',
  previewModelsTitle: 'Draw the props’ photoreal models (Prop Studio’s previews, several MB each) in place of the quick stand-ins. Slower to load and to draw: switch on to check a staging, off to work.',

  // Render controls (the header buttons and their windows). The names of the
  // individual controls live with their limits, in ui/room3d/renderSettings.ts.
  render: {
    looks: 'Looks',
    looksTitle: 'Looks — ready-made lighting setups',
    custom: 'Custom',
    openWindow: (name: string) => `${name} settings`,
    compare: 'Before',
    compareTitle: 'Hold to see the view as it looked before these controls (the Classic look)',
    close: 'Close',
    resetWindow: 'Put everything in this window back to the default look',
    resetValue: 'Back to the default',
    resetAll: 'Reset everything',
    copy: 'Copy settings',
    paste: 'Paste settings',
    apply: 'Apply',
    copied: 'Copied to the clipboard.',
    copyFailed: 'Couldn’t reach the clipboard — copy the text below.',
    pasteHint: 'Paste settings copied from here into the box, then Apply.',
    pasteBad: 'That doesn’t look like render settings.',
    pasteOk: 'Applied.',
    upload: 'Upload…',
    uploads: 'Your uploads',
    removeUpload: 'Remove this upload',
    uploadFailed: 'That picture could not be added.',
    looksNote: 'Your settings are kept in this browser. Tune a look, then Copy settings to share it — or to have it made the default.',

    // The Lighting window: Simple (lay the room's lighting out from a few choices) or Advanced (every setting)
    simple: 'Simple',
    advanced: 'Advanced',
    modeTitle: 'Simple lays out the room’s lighting from a few choices. Advanced has every setting.',
    lights: {
      fixture: 'Fixture',
      fixtures: { pot: 'Pot light', dome: 'Dome', bar: 'Shop light' },
      fixtureHints: {
        pot: 'Recessed in the ceiling, lighting all round.',
        dome: 'A dome light on the ceiling.',
        bar: 'A shop light on chains, hanging below the ceiling.',
      },
      coverage: 'Coverage',
      coverages: { sparse: 'Sparse', balanced: 'Balanced', bright: 'Bright' },
      coverageHint: 'How closely the lights are spaced. The count follows the room: the same choice puts two in a shed and twelve in a shop.',
      brightness: 'Brightness',
      ambient: 'Ambient',
      ambientHint: 'Even light from everywhere, on top of the lamps. Lifts the shadows; too much flattens the room.',
      warmth: 'Warmth',
      warmthNames: { warm: 'Warm', soft: 'Soft white', white: 'White', cool: 'Cool' },
      showFixtures: 'Show the fixtures',
      count: (n: number, what: string, cols: number, rows: number) => `${n} × ${what} — ${cols} across, ${rows} deep`,
      roomSize: (l: number, w: number) => `${l}′ × ${w}′ room`,
      planNote: 'Spaced evenly and symmetrically to fill the room. The layout re-fits itself whenever the room changes.',
      byHand: 'The lights were placed by hand in Advanced. Generate to lay them out as this array.',
      generate: 'Generate lighting',
      regenerate: 'Regenerate',
      regenerateHint: 'Lay the lights out again from these choices, centred in the room, with any hand adjustments from Advanced put back.',
      max: (n: number) => `Up to ${n} lights in a room.`,
    },
  },

  // Sandbox
  preset: 'Room',
  resetRoom: 'Reset room',
  focusRoom: 'Room shown',
  blankPlan: 'Blank plan — draw your own rooms',
  planner: {
    open: 'Floor planner',
    openTitle: 'Floor planner — lay out the rooms from above',
    title: 'Floor Planner',
    back: 'Back to 3D',
    backTitle: 'Back to the 3D view of the room you last clicked',
    select: 'Select',
    selectTitle: 'Select, move and resize rooms',
    draw: 'Draw room',
    drawTitle: 'Drag on the sheet to draw a new room',
    fit: 'Fit',
    fitTitle: 'Fit every room in view',
    name: 'Name',
    length: 'Length',
    width: 'Width',
    height: 'Ceiling',
    delete: 'Delete room',
    deleteMany: (n: number) => `Delete ${n} rooms`,
    openIn3d: 'Open in 3D',
    space: 'Space',
    // The create wizard (a blank one-room plan): planner/CreateRoomWizard.tsx
    create: 'Create room',
    wizard: {
      steps: { name: 'Name', space: 'Space', size: 'Size', ceiling: 'Ceiling', finish: 'Finish' } as Record<'name' | 'space' | 'size' | 'ceiling' | 'finish', string>,
      stepOf: (n: number, of: number) => `Step ${n} of ${of}`,
      back: 'Back',
      next: 'Next',
      nameTitle: 'Let’s get started creating your space!',
      nameIntro: 'Give it a name — anything that helps you find it again. You can change it later.',
      namePlaceholder: 'e.g. My garage',
      spaceTitle: 'What kind of space is this?',
      spaceIntro: 'It sets the room sizes we suggest, and what the 3D view furnishes it with.',
      /** `space` is null for Other: no kind to name. */
      sizeTitle: (space: string | null) => (space ? `Common sizes for a ${space.toLowerCase()}` : 'Common sizes'),
      sizeIntro: 'Pick the closest — you can drag the walls to the exact size on the blueprint.',
      sizeCustomTitle: 'Your room’s dimensions',
      sizeCustomIntro: 'Inside the walls, finished floor to ceiling.',
      custom: 'Enter your own dimensions',
      backToPresets: 'Back to common sizes',
      wallHeight: 'Wall height',
      walls: (h: string) => `${h} walls`,
      ceilingTitle: 'What shape is the ceiling?',
      ceilingIntro: 'Pick the shape, then drag the gold handle — or choose a steepness.',
      finishTitle: 'Dress the walls and ceiling',
      finishIntro: 'A starting point with product on every surface. Fine-tune any wall in 3D.',
      looks: 'Start from a look',
      board: 'Wall&CeilingBoard',
      direction: { Horizontal: 'Horizontal', Vertical: 'Vertical' } as Record<'Horizontal' | 'Vertical', string>,
      slatwall: 'SlatWall band',
      slatwallOn: 'SlatWall on',
      patterns: { all: 'All walls', 'front-back': 'Front & back', sides: 'Sides', back: 'Back wall', none: 'None' } as Record<'all' | 'front-back' | 'sides' | 'back' | 'none', string>,
      custom_walls: 'Your pick',
      bandSize: 'Band height',
      band: (size: string, rows: number) => `${size} · ${rows} row${rows === 1 ? '' : 's'} of 6″`,
      ceiling: 'Ceiling',
      ceilingYes: 'Finish it',
      ceilingNo: 'Leave bare',
      faces: { front: 'Front', right: 'Right', back: 'Back', left: 'Left' } as Record<'front' | 'right' | 'back' | 'left', string>,
      faceToggle: (face: string, on: boolean) => `${face} wall — click to ${on ? 'take the SlatWall off' : 'add the SlatWall band'}`,
      bare: 'No product',
      colors: { White: 'White', Gray: 'Gray' } as Record<'White' | 'Gray', string>,
      createHint: 'Your room opens on the blueprint — check it, then go to 3D.',
      skip: 'Skip — create it with bare walls',
    },
    // The selected room's tools (planner/RoomToolbar.tsx) and the window each opens (planner/RoomEditDialog.tsx)
    tools: {
      label: 'Room tools',
      titles: {
        name: 'Rename the room',
        space: 'Space — what kind of room this is',
        size: 'Size — length, width and wall height',
        ceiling: 'Ceiling — its shape and height',
        finish: 'Finish — product on the walls and ceiling',
      } as Record<'name' | 'space' | 'size' | 'ceiling' | 'finish', string>,
      many: (n: number) => `${n} rooms`,
    },
    edit: {
      nameTitle: 'Name your room',
      nameIntro: 'Anything that helps you find it again.',
      heightManyTitle: 'Wall height',
      heightManyIntro: (n: number) => `Sets the walls of all ${n} selected rooms to one height.`,
      finishIntro: 'Pick a look, or set each part. Apply puts it on the room.',
      finishNote: 'Replaces the product on every wall and the ceiling — windows and doors stay. Undo brings back what was there.',
      apply: 'Apply to the room',
      applyMany: (n: number) => `Apply to ${n} rooms`,
      done: 'Done',
      cancel: 'Cancel',
      close: 'Close',
    },
    designIn3d: 'Design in 3D',
    designIn3dTitle: 'Open the room in 3D to place windows and doors, fine-tune each wall and see it furnished',
    hintCreate: 'Fill in the room to start.',
    hintSelectOne: 'Click the room to edit it. Drag to move, pull a handle to resize, arrows to nudge. Double-click it to design it in 3D.',
    // Advanced mode's row
    reset: 'Reset',
    resetTitle: 'Start this blueprint again from a blank sheet',
    resetConfirm: (name: string) => `Clear ${name} — its room, walls, windows and doors — and start it again from a blank sheet? Undo will not bring it back.`,
    resetNow: 'Clear it',
    help: 'Help',
    helpTitle: 'What the controls do',
    getEstimate: 'Get Estimate',
    getEstimateTitle: 'Your cost and materials estimate for this room',
    getEstimateDisabled: 'Add Wall&CeilingBoard or SlatWall to a wall or the ceiling first',
    preparingEstimate: 'Preparing your estimate…',
    preparingEstimateBody: 'Taking a picture of your room and counting the materials.',
    estimateFailed: 'The room could not be captured. Try again, or open it in 3D first.',
    exit: 'Exit',
    exitTitle: 'Back to the estimator. Your blueprints stay in this browser.',
    selected: (n: number) => `${n} rooms selected`,
    heightAll: 'Sets the ceiling height of every selected room.',
    sharedWall: 'Shared wall',
    sharedWallBetween: (a: string, b: string, length: string) => `${a} and ${b} · ${length} long`,
    removeWall: 'Remove wall',
    restoreWall: 'Restore wall',
    wallRemoved: 'This stretch of wall is open between the two rooms: framed with a header, trimmed, and cut out of the panelling on both sides.',
    wallStands: 'Framed by the older room and panelled from both sides. Remove it to open the two rooms to each other.',
    openingsHere: 'On this wall — click one on the plan, or here',
    openingOnWall: (kind: string) => `${kind} in a shared wall`,
    openingBetween: (a: string, b: string, size: string) => `Between ${a} and ${b} · ${size}`,
    removeBehind: (kind: string) => `Remove the wall behind this ${kind.toLowerCase()}`,
    removeBehindNote: (kind: string) => `Takes out only the part of the wall the ${kind.toLowerCase()} sits in. The ${kind.toLowerCase()} stays, in both rooms; its trim covers the opening. Delete it later in 3D and you choose what is left.`,
    restoreBehind: 'Put the wall back behind it',
    wholeWall: 'Select the whole wall instead',
    pickOpeningTitle: (kind: string) => `${kind} — click to remove just the wall behind it`,
    openBehind: 'Open wall behind it',
    closeBehind: 'Close it up',
    openBehindNote: 'The wall is out behind it. It stays; delete it in 3D and a framed void is left.',
    openBehindHint: 'Opens just the wall behind this one — it stays where it is, and its trim covers the opening in both rooms.',
    removeWarning: (n: number) => `Removing the whole wall also removes the ${n === 1 ? 'window or door' : `${n} windows and doors`} on it, in both rooms.`,
    removeAnyway: 'Remove wall and them',
    keepOne: 'Cancel',
    hintSelect: 'Click a room to select it (Shift-click for more). Drag to move, handles to resize, arrows to nudge. Click a shared wall to open or restore it. Double-click a room to open it in 3D.',
    hintDraw: 'Drag on the sheet to draw a room. It catches on other rooms’ walls to join them; hold Alt for free placement.',
    hintEmpty: 'A blank sheet. Choose Draw room, then drag to draw your first room.',
    sheetTitle: 'FLOOR PLAN',
    rooms: (n: number) => `${n} ROOM${n === 1 ? '' : 'S'}`,
    grid: 'GRID 1 FT',
    ceiling: 'CLG',
    /** The ceiling shape editor (planner/CeilingEditor.tsx). */
    ceilingProfile: {
      title: 'Ceiling shape',
      shapes: {
        flat: { label: 'Flat', builder: 'level', plain: 'Level with the tops of the walls' },
        shed: { label: 'Sloped', builder: 'shed · one slope', plain: 'One side higher than the other' },
        gable: { label: 'Peaked', builder: 'gable · cathedral', plain: 'Rises to a ridge in the middle' },
        vaulted: { label: 'Vaulted', builder: 'flat centre', plain: 'Sloped sides, flat in the middle' },
      } as Record<'flat' | 'shed' | 'gable' | 'vaulted', { label: string; builder: string; plain: string }>,
      flatSummary: (height: string) => `Flat, at ${height}.`,
      flatWizard: (height: string) => `A flat ceiling, level with the tops of the walls at ${height}. Most garages and basements have one.`,
      steepness: 'How steep?',
      steepWords: { 3: 'Gentle', 6: 'Medium', 9: 'Steep', 12: 'Very steep' } as Record<number, string>,
      tooSteep: 'Too tall for this room — the peak would pass 24 ft.',
      exactNumbers: 'Exact numbers',
      summary: (peak: string, pitch: string, word: string) => `Peak ${peak} · ${pitch} pitch, ${word}.`,
      summaryTwo: (peak: string, a: string, b: string) => `Peak ${peak} · ${a} and ${b} pitches.`,
      appliesToAll: (n: number) => `Applies to all ${n} selected rooms.`,
      runs: 'Peak runs',
      axis: { length: 'Lengthways', width: 'Widthways' } as Record<'length' | 'width', string>,
      sectionLabel: 'Cross-section of the room — drag the high point to shape the ceiling',
      across: (across: string, along: string) => `${across} across · ${along} long`,
      handle: 'High point of the ceiling',
      dragHintGable: 'Drag the peak — up: steeper · sideways: off centre',
      dragHintShed: 'Drag the high point — up: steeper · across: swap sides',
      dragHintVaulted: 'Drag the shoulder — up: steeper · sideways: wider or narrower flat',
      flatWidth: 'Flat centre width',
      copyTo: 'Copy this ceiling to',
      copyToAll: 'All other rooms',
      copyToTitle: (name: string) => `Give ${name} this ceiling — its shape and what is laid on it`,
      pitch: 'Pitch',
      pitchWords: { gentle: 'gentle', moderate: 'moderate', steep: 'steep' } as Record<'gentle' | 'moderate' | 'steep', string>,
      customPitch: (pitch: string) => `${pitch} · custom`,
      rise: 'Rise above the walls',
      peak: 'Peak height',
      centre: 'Centre the peak',
      wallsStay: 'The walls keep their height; the ceiling rises above them.',
    },
    refused: {
      overlap: 'Rooms can’t overlap.',
      'too-close': 'Too close for two separate walls — join the rooms or move them further apart.',
      'too-small': 'A room is at least 4 ft each way, with a ceiling of at least 6 ft.',
      'too-large': 'A room is at most 100 ft each way, with a ceiling of at most 24 ft.',
      'not-found': 'That room no longer exists.',
      'bad-profile': 'That ceiling shape doesn’t fit this room.',
      'room-limit': 'This blueprint holds one room. Start another blueprint from Rooms for a second one.',
    } as Record<string, string>,
  },
  /** Deleting a window or door that has the wall taken out behind it. */
  voidPrompt: {
    title: 'This is in a wall between two rooms',
    body: (n: number) => `What should be left where ${n === 1 ? 'it' : 'they'} stood?`,
    leave: 'Leave a framed opening',
    close: 'Close it up — solid wall',
    cancel: 'Cancel — keep it',
  },
  dataRetailer: (id: number) => `Live prices · retailer ${id}`,

  /** Advanced mode (the designer as a 4th project type): the beta hot key's confirmation. */
  advanced: {
    betaOn: 'Advanced mode is on for this browser — it is the fourth card on step 1.',
    betaOff: 'Advanced mode is off for this browser.',
  },
  /** The 3D view's title in Advanced mode. */
  designSpace: 'Design your Space',
  /** The Rooms menu: the visitor's blueprints, one room each. */
  rooms: {
    title: 'Rooms',
    menuTitle: 'Your rooms — each on its own blueprint. The 3D view shows the one you are on.',
    newRoom: 'New room',
    empty: 'not made yet',
    rename: 'Rename',
    renameDone: 'Done',
    delete: 'Delete room',
    deleteConfirm: (name: string) => `Delete ${name} and its blueprint? This cannot be undone.`,
    deleteNow: 'Delete',
    cancel: 'Cancel',
  },
  /** Arriving in Advanced mode with a design kept from last time (advanced/ResumePrompt.tsx). */
  resume: {
    title: 'Welcome back',
    intro: 'Your last design is still saved in this browser. Pick up where you left off, or start a new one.',
    moreRooms: (n: number) => `+ ${n} more room${n === 1 ? '' : 's'}`,
    changed: (when: string) => `Last changed ${when}`,
    justNow: 'just now',
    minutes: (n: number) => `${n} minute${n === 1 ? '' : 's'} ago`,
    hours: (n: number) => `${n} hour${n === 1 ? '' : 's'} ago`,
    yesterday: 'yesterday',
    days: (n: number) => `${n} days ago`,
    on: (date: string) => `on ${date}`,
    startNew: 'Start new',
    startNewNote: 'Starting new clears the saved design.',
    continue: 'Continue design',
  },
  /** The Help window in Advanced mode. */
  help: {
    title: 'Advanced mode',
    intro: 'Make your room on the blueprint, then design its walls and ceiling in 3D. Everything you do is kept in this browser.',
    plannerHeading: 'Floor Planner',
    select: 'Click the room to select it; drag it to move, pull a corner or side to resize. Its tools appear beside it.',
    draw: 'Drag on the sheet to draw another room. It catches on the walls of the rooms already there.',
    fit: 'Bring the whole room into view.',
    reset: 'Clear this blueprint and start it again.',
    space: 'The kind of space the room is — garage, workshop, bathroom… — which sets what it is furnished with.',
    rooms: 'Your rooms, each on its own blueprint: switch between them, rename, delete, or start a new one. The 3D view shows the one you are on.',
    undo: 'Step back or forward through your changes (Ctrl+Z, Ctrl+Y). Each blueprint has its own history while it is open.',
    exit: 'Back to the estimator. Your blueprints stay here for next time.',
    roomHeading: 'The room',
    room: 'A blank blueprint walks you through it: a name, the kind of space, its size, the ceiling — flat, sloped, peaked or vaulted — and a starting finish of product on every wall. Once the room exists, the toolbar beside it opens each of those again (Space, Size, Ceiling, Finish), and the pencil by its name renames it.',
    designIn3d: 'Opens the room in 3D.',
    threeDHeading: 'In 3D',
    threeD: 'Drag to look round the room; scroll to zoom. Click a wall or the ceiling to turn to it and edit it: add sections of Wall&CeilingBoard or SlatWall, windows, doors and a garage door, set each one’s colour and board direction, hang accessories on the SlatWall. Double-click the floor to stand there. The Floor Planner button goes back to the blueprint.',
    stage: 'Furnishes the room with the things found in that kind of space, so you can see it in use. Stage again for another arrangement.',
    keysHeading: 'Keyboard',
    keys: [
      ['Ctrl+Z / Ctrl+Y', 'Undo, redo'],
      ['Delete', 'Delete what is selected'],
      ['Arrow keys', 'Nudge what is selected (Ctrl: 1", Shift: 1 ft)'],
      ['Escape', 'Step back: close a menu, clear the selection, leave a wall'],
    ] as [string, string][],
    done: 'Got it',
  },
  dataFallback: 'Fallback table (API not reachable)',

  /** The Photoreal render window: how Blender renders the room (scripts/blender/RENDER-SETTINGS.md). */
  textureStudio: {
    title: (surface: string) => `Texture Studio — ${surface}`,
    edit: 'Edit…',
    editHint: 'Open the Texture Studio: the set and how it is laid on, with a swatch of this surface rendered as the scene will show it.',
    placed: (p: { scale: number; rotation: number; roughness: number; relief: number; brightness: number; tint?: string; tintStrength?: number }) => `×${p.scale} · ${p.rotation}° · roughness ×${p.roughness} · relief ×${p.relief} · brightness ×${p.brightness}${p.tintStrength ? ` · colour ${p.tint} at ${Math.round(p.tintStrength * 100)}%` : ''}`,
    objects: {
      floor: 'Each room’s floor.',
      wood: 'The body and panels of every door and garage door of a wood style: Shaker walnut, Craftsman oak, Three-quarter oak, Prairie walnut; Carriage walnut, Planks cedar, X-brace grey, Barn cedar. Painted styles keep their paint.',
      fence: 'The yard’s fence: its boards (cut into pickets), rails and posts.',
      lumber: 'Every stick of framing: studs, king studs, cripples, plates, headers, rough sills, the rake plates under a slope, and the bare areas’ studs.',
      frame: 'Every window frame, door jamb and casing, garage door jamb, and every sill.',
      lawn: 'The yard’s lawn, and the grass blades on it (they take the set’s colour).',
      slab: 'The driveway out from each garage door, the apron, and the landing outside each exterior door.',
      drywall: 'The bare areas’ unpainted sheets: a wall or ceiling shown without Trusscore.',
    } as Record<string, string>,
    item: 'Item',
    itemHint: 'One particular style of door, garage door or window, with a placement of its own; or every one of them (the surface’s placement).',
    itemAll: 'Every one (the surface’s placement)',
    itemOwn: 'This item has a placement of its own. The swatch is this item, as the room builds it.',
    itemFromSurface: 'This item follows the surface’s placement until Apply. The swatch is this item, as the room builds it.',
    applyItem: 'Apply to this item',
    applyItemHint: 'This item gets a placement of its own; the others keep the surface’s.',
    removeItem: 'Back to the surface’s',
    removeItemHint: 'Take this item’s own placement away: the surface’s applies to it again.',
    set: 'Set',
    noSet: 'No set (procedural)',
    fieldHints: {
      scale: 'The tile this many times its real size: 0.5 halves a grain, 2 doubles it.',
      rotation: 'The picture turned on the surface: 90 runs a grain across instead of along.',
      roughness: 'Times the set’s roughness: under 1 glossier, over 1 more matte.',
      relief: 'Times the bump its normal and displacement make: 0 is flat.',
      brightness: 'Times its colour.',
      tint: 'A colour laid over the picture: it keeps its light and dark and takes the colour’s hue and saturation (a grey carpet made blue). Pick a paler colour for a gentler cast.',
      tintStrength: 'How far the colour is applied: 0 leaves the picture as shot, 1 is all the way. Set it deliberately — a little goes a long way.',
    } as Record<string, string>,
    object: 'Shown on',
    objectDoor: 'A door',
    objectGarage: 'A garage door',
    spaceHint: 'The floor is judged in this kind of space: its wall base stands behind the floor in the swatch.',
    auto: 'Render after each change',
    autoHint: 'A swatch takes a few seconds on the back end’s Blender. Off: the ↻ button renders one.',
    compare: 'Compare with procedural',
    compareHint: 'A second swatch of the same object with the set left out: the renderer’s own finish.',
    apply: 'Apply',
    applyHint: 'Put this placement into the photoreal settings (the picker follows).',
    saveToPool: 'Save to pool',
    saveToPoolHint: 'Append this placement to the chosen space’s Floors by space (then Save there).',
    savedToPool: (space: string) => `Added to ${space}’s floors — press Save under Floors by space to keep it.`,
    remove: 'Procedural',
    removeHint: 'This surface back to the renderer’s own finish.',
    current: 'This placement',
    cached: '(already rendered)',
    procedural: 'Procedural',
    renderNow: 'Render the swatch now',
    rendering: 'Rendering…',
    noSwatchYet: 'No swatch yet',
    swatchAlt: 'The surface with this placement',
    plainAlt: 'The surface with the procedural finish',
    pin: 'Pin this swatch beside the next ones',
    pinnedHint: 'Click to put this placement back',
    note: 'The swatch is the renderer’s own material code on this surface’s object, lit by the Lighting tab’s settings: what a scene render will show. A few seconds each; one already rendered is shown at once.',
    liveNote: 'Live in your browser, as you move the sliders: the set at its real size, lit by this look’s HDRI. Drag to look around. Judge scale, pattern and tone here; Test render for the last word on sheen.',
    blenderShow: 'Blender swatch…',
    blenderHide: 'Hide the Blender swatch',
    blenderHint: 'A swatch rendered by Blender on the back end (the renderer’s own material code): only where the back end has Blender (this PC’s props helper)',
  },
  photo: {
    title: 'Photoreal render',
    openTitle: 'Photoreal render: how Blender renders the room, and the Render button',
    tabs: { materials: 'Materials', textures: 'Textures', camera: 'Camera', lighting: 'Lighting', effects: 'Effects', output: 'Output' } as Record<string, string>,
    render: 'Render',
    rendering: 'Saving…',
    renderTitle: 'Save the room (room.glb) with its camera, lights and these settings (room.render.json) for the Blender renderer',
    rendered: 'Saved room.glb and room.render.json — render them with scripts\\blender\\render.ps1.',
    preview: 'Preview in the 3D view',
    previewHint: 'Show these settings, roughly, in the live view: each change nudges the view the same way and by about as much. A rough guide — the render is the real thing.',
    previewNote: (missing: string) => `Previewing: finishes’ roughness, joints, floor, sun, sky, lamps, exposure, colour, contact shading, bloom, vignette, softness, film grain. Not shown live: ${missing}.`,

    wcb: 'Wall&CeilingBoard',
    slatwall: 'SlatWall',
    trim: 'Trim',
    floor: 'Floor',
    roughness: 'Roughness',
    roughnessHint: 'How blurred its reflections are: 0 a mirror, 1 fully matte. Lower is shinier.',
    specular: 'Specular',
    specularHint: 'How strongly it reflects light head-on (0.5 is ordinary plastic).',
    coat: 'Clear coat',
    coatHint: 'A thin glossy layer on top — the crisp highlight of a smooth PVC face.',
    coatRoughness: 'Coat roughness',
    coatRoughnessHint: 'How sharp the clear coat’s highlight is: lower is crisper.',
    joints: 'Board joints',
    jointsHint: 'How much the joints between boards show in the render: 0 hides them (the boards shade as one face), 1 the full profile.',
    jointsFollow: (seam: string) => `Joints follow the seam Strength (${seam})`,
    jointsFollowLabel: 'Board joints follow the seam Strength',
    relief: 'Relief',
    reliefHint: 'How much the floor’s texture is raised into bumps.',

    cameraLensGroup: 'Lens',
    cameraLensNote: 'A longer lens shows less from the same spot, as a real one does: step the camera back to frame it. Background blur comes mostly from a longer lens and a near focus (50 mm, f/2, focused 6 ft away), not the f-stop alone.',
    lensType: 'Lens type',
    lensTypeHint: 'Normal, or a 180° fisheye (curved, very wide). For a 360 of the whole room, choose it under Output → Pictures.',
    lensTypes: { perspective: 'Normal', fisheye: 'Fisheye (180°)' } as Record<string, string>,
    focal: 'Focal length',
    focalHint: 'Full-frame millimetres (36 mm across the picture’s longer side). View: the 3D view’s own, as it is.',
    focalView: (mm: string) => `View${mm ? ` (≈ ${mm} mm)` : ''}`,
    focalOther: 'Other…',
    focalFisheye: 'Fisheye focal length',
    focalFisheyeView: 'Full frame (15 mm)',
    fisheyeNote: 'A fisheye bends straight lines into curves toward its edges: striking in a tight space, strange in a large one. 15 mm fills the frame; 8–10 mm draws the round image with black around it.',
    previewLens: 'With Preview on, the 3D view shows this lens.',

    cornerGroup: 'Corner views',
    cornerNote: 'The four pictures taken from the room\'s corners (Output: Pictures, all). Each is aimed at the middle of the room.',
    cornerVary: 'Camera height, up or down',
    cornerVaryHint: 'How far each corner camera may wander from eye height, drawn by the render\'s seed and kept inside the room. Higher looks down at the room, lower looks up. 0: every corner camera at eye height.',
    cornerClear: 'Move a blocked camera',
    cornerClearHint: 'When something stands in the corner and fills the frame — a coat rack, a car, a cabinet — the camera moves in along its walls to the first clear spot.',
    dofGroup: 'Depth of field',
    dofNote: 'As through a real lens: only what is at the focus distance is sharp.',
    dof: 'Depth of field',
    dofHint: 'Blur what is nearer or farther than the focus distance.',
    fStop: 'f-stop',
    fStopHint: 'The aperture: smaller numbers blur the background more (f/1.4 very shallow, f/8 nearly all sharp).',
    autoFocus: 'Focus on the centre of the view',
    autoFocusHint: 'Focus on whatever is in the middle of each picture.',
    focus: 'Focus distance',
    focusHint: 'How far from the camera is sharp.',
    bokehGroup: 'Bokeh',
    bokehNote: 'The shape of out-of-focus highlights.',
    blades: 'Aperture blades',
    bladesHint: '0 round; 3 or more a polygon with that many sides.',
    bladeRotation: 'Blade rotation',
    bokehRatio: 'Anamorphic ratio',
    bokehRatioHint: '1 round; above 1 tall ovals, below 1 wide ones (a cinema lens).',

    outside: 'Light from outside',
    world: 'Light',
    worldHint: 'What surrounds the room and shines in through its windows.',
    worldSky: 'Pale sky (as before)',
    worldHdri: (file: string) => `HDRI: ${file}`,
    worldHdriNone: 'HDRI (pick one below)',
    skyStrength: 'Sky strength',
    hdriStrength: 'HDRI strength',
    hdriRotation: 'HDRI rotation',
    hdriRotationHint: 'Turn the HDRI to bring its sun round to another window.',
    hdriVisible: 'Seen through the windows',
    hdriVisibleHint: 'Off: the HDRI lights the room, but the windows show the plain sky.',
    hdriLibrary: 'HDRI library',
    hdriLibraryNote: 'A photo of a real sky all round, lighting the room as that place would. Pick one, fetch a free one from Poly Haven, or upload your own .hdr / .exr.',
    hdriLoading: 'Loading the library…',
    hdriEmpty: 'Nothing here yet — fetch one from Poly Haven below, or upload one.',
    hdriNoBackEnd: 'The render library is not reachable: on the built site sign in as super admin; under npm start run node scripts/props/server.js.',
    hdriUpload: 'Upload .hdr / .exr',
    hdriUploading: 'Uploading…',
    hdriRemove: (file: string) => `Remove ${file} from the library`,
    hdriRemoveConfirm: (file: string) => `Remove ${file} from the HDRI library? The file is deleted.`,
    hdriSearchPlaceholder: 'Search Poly Haven (sunny, overcast, studio…)',
    hdriSearch: 'Search Poly Haven',
    hdriResolution: 'Size to fetch',
    hdriResolutionHint: '2K is plenty for lighting; 4K if the windows show it close up.',
    hdriNothingFound: 'Nothing on Poly Haven matches.',
    hdriFetching: 'Fetching…',
    polyHavenNote: 'Poly Haven HDRIs are CC0: free for any use, commercial included, no credit needed. Click one to add it.',
    texturesGroup: 'Photographic textures',
    texturesNote: 'Real photographed surfaces from the texture library, each tiled at its true size, in place of the renderer’s own finishes. Render only: the 3D view keeps its look.',
    textureLawn: 'Lawn',
    textureLawnHint: 'The yard’s grass: the ground under the blades, and the blades take its colour.',
    textureWood: 'Doors & garage doors',
    textureWoodHint: 'Doors and garage doors of the wood styles (walnut, oak, cedar…). Painted styles stay painted.',
    textureFence: 'Fence',
    textureLumber: '2x4 framing',
    textureFrame: 'Window & door frames',
    textureFrameHint: 'The frames and sills of windows and doors. None: painted, as the style says.',
    textureFloor: 'Floor',
    textureFloorHint: 'The room’s floor, in place of the floor picture: its tiles at their true size × Tile scale. Procedural: the floor picture as before. Stage fills it from Floors by space.',
    textureSlab: 'Driveway & landings',
    textureSlabHint: 'The yard’s concrete: the driveway out from each garage door, the apron, the landing outside each exterior door. Procedural: broom-finished concrete with expansion joints.',
    textureDrywall: 'Drywall',
    textureDrywallHint: 'The bare areas’ unpainted drywall sheets (a wall or ceiling shown without Trusscore). Procedural: the view’s own sheet picture.',
    textureNone: 'Procedural (the renderer’s own)',
    texturePick: 'Pick a texture',
    texturePickHint: 'Opens the library: every set as a picture.',
    texturePickSearch: 'Search the library by name…',
    texturePickNone: 'None',
    texturePickNoMatch: 'Nothing in the library matches.',
    finishFloorGroup: 'Floors by space',
    finishFloorNote: 'The floor sets a kind of space is rendered with, each with its own tile scale, roughness and relief. Stage draws one of them for the plan’s space (while Floor above is Procedural). Add keeps the Floor chosen above with the floor settings as an entry; Try puts an entry back into these settings; Save writes the space’s entries to the library.',
    finishHdriGroup: 'HDRIs by space',
    finishHdriNote: 'The HDRIs a kind of space is rendered under, each with its own strength and turn. Stage draws one for the plan’s space (while no HDRI is chosen above) and lights the room with it. Add keeps the HDRI chosen above with its settings; Try puts an entry back; Save writes the space’s entries to the library.',
    finishSpace: 'Space',
    finishEntry: 'Entry',
    finishLoading: 'Loading the library…',
    finishNone: 'No entries for this space: Stage leaves the setting as it is.',
    finishAdd: 'Add these settings',
    finishAddFloorHint: 'Adds the Floor chosen above (or the library’s first set) with the roughness, relief and tile scale set here.',
    finishAddHdriHint: 'Adds the HDRI chosen above (or the library’s first) with the strength and turn set here.',
    finishTry: 'Try',
    finishTryHint: 'Put this entry into the settings: the Render button then uses it.',
    finishRemoveEntry: 'Remove this entry',
    finishSave: 'Save',
    finishSaving: 'Saving…',
    finishSaved: 'Saved to the library. The next Stage draws from it.',
    finishField: { scale: 'Tile scale', rotation: 'Rotation', roughness: 'Roughness', relief: 'Relief', brightness: 'Brightness', tint: 'Colour', tintStrength: 'Colour strength', strength: 'Strength' } as Record<string, string>,
    finishStagedFloor: (id: string, scale: number) => `From the staging: ${id} ×${scale}`,
    finishStagedHdri: (file: string) => `The staging lights the room with ${file} (no HDRI chosen here); choose one to use it instead.`,
    textureSize: (w: number, h: number) => `${w.toFixed(1)} × ${h.toFixed(1)} ft`,
    textureFind: 'Find textures',
    textureFindNote: 'Search Poly Haven or BlenderKit. Click a result to fetch it: it joins the pickers above.',
    textureLibrary: 'Downloaded textures',
    textureLibraryNote: 'The sets in scripts/blender/textures, the ones the pickers above list. The bin removes one.',
    textureLoading: 'Loading the library…',
    textureEmpty: 'Nothing here yet — search Poly Haven below and fetch a set.',
    textureRemove: (id: string) => `Remove ${id} from the library`,
    textureRemoveConfirm: (id: string) => `Remove the texture set ${id} from the library? Its files are deleted.`,
    textureSearchPlaceholder: 'Search Poly Haven (grass, planks, cedar…)',
    textureSearchBkPlaceholder: 'Search BlenderKit materials (lawn, oak floor, tiles…)',
    textureSource: 'Search',
    textureSourceHint: 'Poly Haven: ready-made texture sets, all CC0. BlenderKit: its materials, which the back end’s Blender bakes into a set (colour, roughness, normal with every bump, height) — a minute or two each. Your BlenderKit key (scripts/blender/props/.blenderkit-key) opens the paid ones.',
    textureSourcePolyHaven: 'Poly Haven textures',
    textureSourceBlenderKit: 'BlenderKit materials',
    textureSourceBlenderKitFree: 'BlenderKit materials (free only)',
    textureNoBlender: 'BlenderKit is not offered here: its materials are Blender files, and this server has no Blender to turn them into texture maps. Add BlenderKit floors from the Prop Studio on a PC with Blender.',
    textureBkCategory: 'Category',
    textureBkCategoryHint: 'One of BlenderKit’s material categories; with one chosen the search text may be empty.',
    textureBkCategories: { grass: 'Grass', dirt: 'Dirt', floor: 'Floor', wood: 'Wood', concrete: 'Concrete', bricks: 'Bricks', tiles: 'Tiles', ceramic: 'Ceramic', stone: 'Stone', rock: 'Rock', marble: 'Marble', plaster: 'Plaster', asphalt: 'Asphalt', metal: 'Metal', fabric: 'Fabric' } as Record<string, string>,
    textureMore: (n: number) => `More results (${n} so far)`,
    textureBkFree: 'Free',
    textureBkPlan: 'Royalty free (plan)',
    textureBaking: 'Baking…',
    textureNothingFoundBk: 'Nothing on BlenderKit matches.',
    blenderKitTexturesNote: 'A BlenderKit material is downloaded as a .blend and baked into a texture set by Blender on the back end: its colour, roughness, a normal map carrying its bumps and displacement, and its height. Free materials are CC0; the rest are royalty free under your BlenderKit plan.',
    textureSearch: 'Search Poly Haven textures',
    textureKind: 'Kind',
    textureKindAny: 'Any',
    textureKindGrass: 'Grass & ground',
    textureKindWood: 'Wood',
    textureResolution: 'Size to fetch',
    textureResolutionHint: '2K is plenty; 4K for a surface seen close up. Several maps each, tens of MB.',
    textureNothingFound: 'Nothing on Poly Haven matches.',
    textureFetching: 'Fetching…',
    polyHavenTexturesNote: 'Poly Haven textures are CC0: free for any use, commercial included, no credit needed. Click one to add it.',
    viewGroup: 'View outside',
    viewNote: 'What the windows and doors look out on: a real-world photo round the house, its ground meeting your floor and its horizon at eye height. Seen only through the glass — it never changes the lighting.',
    viewEnabled: 'Show a view outside',
    viewEnabledHint: 'A ground-projected backdrop: a big dome round the house with the photo’s ground laid at floor level. Off: the windows show the lighting HDRI (or the sky) as before. The render’s yard (lawn, driveway, fence, trees — never drawn in the view) stands in front of it, and the photo shows beyond the lawn.',
    viewImage: 'Picture',
    viewImageHint: 'Any HDRI in the library. An outdoor one (street, garden, suburb) at 4K or 8K looks best up close through glass.',
    viewSameAsLighting: (file: string) => `Same as the lighting (${file})`,
    viewSameAsLightingNone: 'Same as the lighting (none: pick one)',
    viewNoImage: 'The lighting is the plain sky: pick a picture for the view outside, or it is left out.',
    viewHeight: 'Photo height',
    viewHeightHint: 'How high the photo was taken: where its horizon sits. Poly Haven shoots at about 5–6 ft.',
    viewRadius: 'Distance',
    viewRadiusHint: 'How far out the backdrop stands. Farther makes nearby things in the photo (trees, houses) feel farther away.',
    viewRotationFollow: 'Turn with the lighting HDRI',
    viewRotationFollowHint: 'Keep the view lined up with the lighting, so the sun in the picture is where the light comes from.',
    viewRotation: 'Turn',
    viewStrength: 'Brightness',
    sunGroup: 'Sun',
    sun: 'Sun',
    sunHint: 'Auto: through the biggest window when the light is the sky (an HDRI brings its own sun).',
    sunAuto: 'Auto',
    sunOn: 'On',
    sunOff: 'Off',
    sunStrength: 'Strength',
    sunElevation: 'Height',
    sunElevationHint: 'Degrees above the horizon: low is long patches across the floor.',
    sunSwing: 'Swing',
    sunSwingHint: 'Degrees off straight in through the window.',
    sunSoftness: 'Softness',
    sunSoftnessHint: 'The sun’s size in the sky: bigger is softer shadow edges.',
    lampsGroup: 'Ceiling lamps',
    lampFixture: 'Fixture',
    lampFixtureHint: 'What hangs at each lamp. Pot: recessed, lighting all round. Dome: on the ceiling. Shop light: on chains, below the ceiling.',
    lampSpacing: 'Spacing',
    lampSpacingHint: 'About one lamp every so many feet, each way. The count follows the room: the same spacing puts two in a shed and twelve in a shop.',
    lampWarmth: 'Warmth',
    lampWarmthHint: 'Colour temperature. 6500 K is pure white and tints nothing; lower is warmer.',
    lampFixtures: 'Show the fixtures',
    lampFixturesHint: 'The fixture drawn at each lamp, glowing. Off: the light with nothing to see at the ceiling.',
    lampScale: 'Brightness',
    lampScaleHint: 'The ceiling lamps: 1 is standard for the room’s size. The view’s own lighting panel does not come into it.',

    toneGroup: 'Exposure and colour',
    autoExposure: 'Auto exposure',
    autoExposureHint: 'Metered from the picture, in the render and in the 3D view alike: the lit boards are brought to one level whatever the HDRI and lamp strengths, which then only set the balance of the light. Off: the renderer’s fixed exposure.',
    exposure: 'Exposure',
    exposureHint: 'Brighter or darker, in stops. The view’s own exposure does not come into it.',
    exposureHintMetered: 'Brighter or darker, in stops, on top of the metered exposure.',
    view: 'Colour handling',
    viewHint: 'AgX keeps bright whites from blowing out (as before); Filmic is softer; Standard is plain.',
    contrast: 'Contrast',
    saturation: 'Saturation',
    shadingGroup: 'Shading',
    contact: 'Contact shading',
    contactHint: 'The darkening where surfaces meet (corners, under things): 0 none, 1 as before.',
    lensGroup: 'Lens effects',
    lensNote: 'Off at 0. Small amounts read as a photograph; a lot reads as an effect.',
    softness: 'Softness',
    softnessHint: 'A lens is never pixel-sharp: the picture blurred a touch (1 is a few pixels). Try 0.2 to 0.4.',
    grain: 'Film grain',
    grainHint: 'Sensor noise, finer than a pixel, stronger in the shadows. Try 0.2 to 0.4; 1 is a high-ISO night shot.',
    bloom: 'Bloom',
    bloomHint: 'A soft glow round the brightest highlights.',
    vignette: 'Vignette',
    dispersion: 'Colour fringing',
    dispersionHint: 'Chromatic aberration: coloured edges towards the corners.',

    outputGroup: 'Output',
    samples: 'Samples',
    samplesHint: 'More is cleaner and slower (256 as before).',
    denoise: 'Denoise',
    denoiseHint: 'Clean the grain at the end: cleaner at low samples, a touch softer.',
    width: 'Width',
    widthHint: 'Pixels across, 640 to 7680. Type a number; Enter or click away to set it.',
    aspect: 'Shape',
    aspectHint: 'The picture’s width to height. The camera keeps the view’s height of view, so a wider shape shows more at the sides and a narrower one less.',
    aspectView: 'View (as the 3D view is now)',
    size: (w: number, h: number) => `${w} × ${h} px`,
    sizeFollowsView: (w: number) => `${w} px wide; the height follows the 3D view’s shape`,
    exportConfig: 'Export Config',
    exportConfigTitle: 'Save every photoreal setting to a .json file',
    importConfig: 'Import Config',
    importConfigTitle: 'Load a saved .json config (or an export’s room.render.json): every setting is set from it',
    importedConfig: (name: string) => `Loaded ${name}: every setting is set from it (anything it lacks is the factory value).`,
    importNotJson: (name: string) => `${name} is not a JSON file.`,
    importNotSettings: (name: string) => `${name} has no photoreal render settings in it.`,
    importMissingHdri: (file: string) => `It lights the room with ${file}, which is not in this render library: add it on the Lighting tab, or the render uses the sky.`,
    importMissingTexture: (ids: string) => `It uses texture sets not in this library (${ids}): fetch them on the Textures tab, or those surfaces render with the procedural finish.`,
    views: 'Pictures',
    viewsUser: 'This view only',
    viewsAll: 'This view and four corner views',
    viewsPano: 'High-res 360 only',
    viewsAllPano: 'This view, 4 corners and 360',
    panoWidth: '360 resolution',
    panoWidthHint: 'The 360 is twice as wide as tall. 8192 is crisp in a 1080p viewer window; 4096 is a quick look. It takes about 11× as long as a 2400-wide picture.',
    panoWidths: { 4096: '4096 × 2048 (quick)', 6144: '6144 × 3072', 8192: '8192 × 4096 (crisp)' } as Record<number, string>,
    panoNote: 'The 360 is taken from the middle of the room at eye height, dead level, opening on the garage door’s wall (a bathroom’s vanity, a laundry’s washer, a living space’s TV), else the front wall. Depth of field, the lens, Shape, vignette and colour fringing are left out of it; your other pictures keep them.',
  },
} as const;
